import { useState, useEffect, useMemo, useCallback } from "react";
import DraggableModal from "../DraggableModal";
import SearchSelect from "../SearchSelect";
import { PhoneField, PHONE_COUNTRIES, parsePhone as parsePhoneShared, serializePhone } from "../PhoneField";
import { openGrowerAuditPreview, downloadGrowerAuditPdf, downloadGrowerAuditXLSX } from "../growerAudit";
import { loadFromSupabase, saveToSupabase } from "../../supabaseDB";
import { hasPermission } from "../../permissions";

/* ============================================================================
 * ShipperGrowers — grower registry scoped to the current shipper.
 *
 * Implements the structured grower form per the V2 spec:
 *   - Identity (Individual / Business)
 *   - Producer Code
 *   - Season (start year + end year, validated as "YYYY-YYYY+1")
 *   - Products delivered (table of {product, category, complianceStatus})
 *   - Required certifications (auto-derived from products)
 *   - Ranchos (multi-row with name + address)
 *
 * Lockout / Request Change / Compliance status from external certs are
 * scaffolded — the data fields exist on the saved record so an admin
 * follow-up can wire the per-product compliance check + lockout logic
 * against the shipments + certifications tables.
 *
 * Linked Shipper field is REMOVED from the UI; saving auto-attaches the
 * current supplierId from the shell context.
 * ============================================================================ */

const T = {
  en: {
    title: "Growers", sub: "Producers that deliver to your operation. Linked to your account automatically.",
    add: "+ New Grower", search: "Search by name or code…",
    columns: { name: "Name", code: "Producer #", season: "Season", products: "Products", hectares: "Hectares", compliance: "Compliance", status: "Status" },
    delete_full: "Delete",
    status_active: "Active", status_inactive: "Inactive",
    status_paused: "Paused", status_archived: "Archived",
    compliance_ok: "OK", compliance_pending: "Pending",
    form_new: "New grower", form_edit: "Edit grower",
    section_identity: "Identity", section_code: "Producer code", section_season: "Season",
    section_products: "Products delivered", section_certs: "Required certifications", section_ranchos: "Ranchos",
    personType: "Person type", individual: "Individual (Persona Física)", business: "Business (Persona Moral)",
    nombre: "Name(s)", apPaterno: "Last name (paternal)", apMaterno: "Last name (maternal)",
    razonSocial: "Business name (Razón Social)",
    section_fiscal: "Fiscal Address (Domicilio Fiscal)",
    fiscalStreet: "Street and/or reference", fiscalMunicipio: "Municipality / locality", fiscalCity: "City",
    fiscalState: "State", fiscalZip: "Zip code", fiscalCountry: "Country",
    copyFiscalToRancho: "Copy from fiscal address",
    fiscalReq: "The fiscal address (at least street) is required.",
    producerCode: "Producer code", producerCodeSub: "Internal identifier in your records.",
    yearFrom: "Year start", yearTo: "Year end", seasonHint: "Optional — example: 2025-2026. The grower stays active regardless of season.",
    addProduct: "+ Add product", product: "Product", category: "Category", organic: "Organic", conventional: "Conventional",
    pickProduct: "Pick a product…",
    variety: "Variety", selectVariety: "Select variety", otherVariety: "Other (type it in)",
    blackberry: "Blackberry", blueberry: "Blueberry", raspberry: "Raspberry",
    addRancho: "+ Add rancho", ranchoName: "Rancho name",
    ranchoStreet: "Street and/or reference",
    ranchoMunicipio: "Municipality / locality",
    ranchoCity: "City",
    ranchoState: "State",
    ranchoZip: "Zip code",
    ranchoCountry: "Country",
    ranchoNotes: "Notes (how to reach the farm — optional)",
    ranchoHectares: "Hectares",
    ranchoPhone: "Phone (optional)", ranchoEmail: "Email (optional)",
    section_contact: "Contact",
    cert_safety: "Food Safety Certificate", cert_audit: "Audit Report", cert_corrective: "Corrective Actions", cert_fsma: "FSMA Report",
    cert_organic: "Valid Organic Certificate (or Letter of Good Standing)", cert_organicAudit: "Organic Audit Report",
    save: "Save", cancel: "Cancel", saving: "Saving…", close: "Close", editBtn: "Edit", phone: "Phone",
    delete: "Deactivate",
    req: "All required fields must be filled.",
    seasonInvalid: "Year end must be year start + 1.",
    productReq: "At least one product is required.",
    ranchoReq: "At least one rancho with name and address is required.",
    locked: "This grower is locked because there is at least one submitted shipment in the current season.",
    requestChange: "Request Change",
    requestChangeTitle: "Request a change",
    requestChangeSub: "Describe what you need to change and why. GFI staff will review your request.",
    requestSent: "Request sent.",
    empty: "No growers yet.",
    inactive: "Inactive (kept for history)",
  },
  es: {
    title: "Productores", sub: "Productores que entregan a tu operación. Se vinculan automáticamente a tu cuenta.",
    add: "+ Nuevo Productor", search: "Buscar por nombre o código…",
    columns: { name: "Nombre", code: "No. Productor", season: "Temporada", products: "Productos", hectares: "Hectáreas", compliance: "Cumplimiento", status: "Estado" },
    delete_full: "Eliminar",
    status_active: "Activo", status_inactive: "Inactivo",
    status_paused: "Pausado", status_archived: "Archivado",
    compliance_ok: "OK", compliance_pending: "Pendiente",
    form_new: "Nuevo productor", form_edit: "Editar productor",
    section_identity: "Identidad", section_code: "Código del productor", section_season: "Temporada",
    section_products: "Productos que entrega", section_certs: "Certificaciones requeridas", section_ranchos: "Ranchos",
    personType: "Tipo de persona", individual: "Persona Física", business: "Persona Moral",
    nombre: "Nombre(s)", apPaterno: "Apellido paterno", apMaterno: "Apellido materno",
    razonSocial: "Razón Social",
    section_fiscal: "Domicilio Fiscal",
    fiscalStreet: "Calle y/o referencia", fiscalMunicipio: "Municipio y/o localidad", fiscalCity: "Ciudad",
    fiscalState: "Estado", fiscalZip: "Código postal", fiscalCountry: "País",
    copyFiscalToRancho: "Copiar del domicilio fiscal",
    fiscalReq: "El domicilio fiscal (al menos la calle) es obligatorio.",
    producerCode: "Código del productor", producerCodeSub: "Identificador interno en tus registros.",
    yearFrom: "Año inicio", yearTo: "Año fin", seasonHint: "Opcional — ejemplo: 2025-2026. El productor se mantiene activo sin importar la temporada.",
    addProduct: "+ Agregar producto", product: "Producto", category: "Categoría", organic: "Orgánico", conventional: "Convencional",
    pickProduct: "Selecciona un producto…",
    variety: "Variedad", selectVariety: "Seleccionar variedad", otherVariety: "Otra (escribir)",
    blackberry: "Zarzamora", blueberry: "Arándano", raspberry: "Frambuesa",
    addRancho: "+ Agregar rancho", ranchoName: "Nombre del predio",
    ranchoStreet: "Calle y/o referencia",
    ranchoMunicipio: "Municipio y/o localidad",
    ranchoCity: "Ciudad",
    ranchoState: "Estado",
    ranchoZip: "Código postal",
    ranchoCountry: "País",
    ranchoNotes: "Notas (cómo llegar al predio — opcional)",
    ranchoHectares: "Hectáreas",
    ranchoPhone: "Teléfono (opcional)", ranchoEmail: "Correo (opcional)",
    section_contact: "Contacto",
    cert_safety: "Certificado de Inocuidad", cert_audit: "Reporte de Auditoría", cert_corrective: "Acciones Correctivas", cert_fsma: "Reporte FSMA",
    cert_organic: "Certificado Orgánico Vigente (o Letter of Good Standing)", cert_organicAudit: "Reporte de Auditoría Orgánica",
    save: "Guardar", cancel: "Cancelar", saving: "Guardando…", close: "Cerrar", editBtn: "Editar", phone: "Teléfono",
    delete: "Desactivar",
    req: "Todos los campos obligatorios deben llenarse.",
    seasonInvalid: "Año fin debe ser año inicio + 1.",
    productReq: "Al menos un producto es obligatorio.",
    ranchoReq: "Al menos un rancho con nombre y dirección es obligatorio.",
    locked: "Este productor está bloqueado porque hay al menos un embarque enviado en la temporada activa.",
    requestChange: "Solicitar Cambio",
    requestChangeTitle: "Solicitar un cambio",
    requestChangeSub: "Describe qué necesitas cambiar y por qué. El equipo de GFI revisará la solicitud.",
    requestSent: "Solicitud enviada.",
    empty: "Aún no hay productores.",
    inactive: "Inactivo (mantenido como histórico)",
  },
};

const inp = { padding: "8px 10px", fontSize: 13, border: "1px solid var(--color-border)", borderRadius: 5, background: "var(--color-bg-input)", color: "var(--color-text)", outline: "none", fontFamily: "inherit", width: "100%", boxSizing: "border-box" };
const errS = { borderColor: "var(--color-danger)" };

const EMPTY_RANCHO = {
  name: "",
  /* Structured address per FIX 15 + 32. `address` (string) is kept
     on legacy records and shown read-only if present, but new
     records write the structured fields. */
  street: "", municipio: "", city: "", state: "", zip: "", country: "México",
  notes: "",
  hectares: "",
};
/* 2026-10-26, Arturo: "el productor persona física o moral puede
   tener un domicilio fiscal que no es precisamente el del rancho...
   necesito un domicilio fiscal y la opcion de que al agregar el
   domicilio de los ranchos pueda copiar del domicilio fiscal si
   fuese el mismo o ingresar uno distinto" — same structured shape as
   a rancho address (no `name`/`hectares`, those are rancho-only
   concepts), stored once on the grower itself since it belongs to
   the persona física/moral, not to any single rancho. */
const EMPTY_FISCAL = { street: "", municipio: "", city: "", state: "", zip: "", country: "México" };
/* 2026-09-28 (Arturo: "al dar de alta un productor debo poder
   agregar la variedad del producto que entregara") — same base
   varieties + "custom variety" storage key (gfi-custom-varieties) as
   CertificationRegistry.js's VarietyField, so a variety typed once
   here (while registering a grower) is immediately offered as an
   option there too (while capturing that grower's certificate), and
   vice versa — one shared list, not two silos. */
const VARIETY_MAP = {
  /* 2026-09-29 (Arturo: "es tuppy con 2 P") — matches the real variety
     name already stored in the commodities table (confirmed directly
     against production data), not the "Tupy" typo this list
     previously had. */
  Blackberry: ["Tuppy", "Erandy"],
  Blueberry: ["Biloxi", "Rabbit Eye"],
  Raspberry: ["Centennial", "Malu", "Driscoll's Maravilla"],
};
function getVarietyOptions(product) {
  const base = VARIETY_MAP[product] || [];
  try {
    const custom = JSON.parse(localStorage.getItem("gfi-custom-varieties") || "{}");
    const extra = custom[product] || [];
    return [...new Set([...base, ...extra])];
  } catch { return base; }
}
function saveCustomVariety(product, variety) {
  try {
    const custom = JSON.parse(localStorage.getItem("gfi-custom-varieties") || "{}");
    if (!custom[product]) custom[product] = [];
    if (!custom[product].includes(variety) && !(VARIETY_MAP[product] || []).includes(variety)) {
      custom[product].push(variety);
      localStorage.setItem("gfi-custom-varieties", JSON.stringify(custom));
    }
  } catch {}
}
const EMPTY_PRODUCT = { product: "", category: "Organic", variety: "" };
const EMPTY = {
  personType: "fisica",
  nombre: "", apPaterno: "", apMaterno: "", razonSocial: "",
  domicilioFiscal: Object.assign({}, EMPTY_FISCAL),
  producerCode: "",
  seasonYearFrom: "", seasonYearTo: "",
  /* Contact fields (FIX 17 — both optional). */
  phoneCountry: "52", phoneArea: "", phoneNumber: "",
  email: "",
  products: [],
  ranchos: [],
  changeRequests: [],
  isActive: true,
  shipperId: null,
};

function getFullName(g) {
  if (g.personType === "moral") return g.razonSocial || "—";
  return [g.nombre, g.apPaterno, g.apMaterno].filter(Boolean).join(" ") || "—";
}
function seasonLabel(g) {
  if (!g.seasonYearFrom && !g.seasonYearTo) return "—";
  return (g.seasonYearFrom || "?") + "-" + (g.seasonYearTo || "?");
}
function summarizeProducts(g) {
  if (!g.products || g.products.length === 0) return "—";
  return g.products.map(function (p) {
    return (p.product || "?") + (p.variety ? " " + p.variety : "") + (p.category === "Conventional" ? " (CV)" : " (OG)");
  }).join(", ");
}
function Field({ label, sub, required, error, children }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <label style={{ fontSize: 12, fontWeight: 600, color: "var(--color-text-secondary)" }}>
        {label}{required && <span style={{ color: "var(--color-danger)", marginLeft: 3 }}>*</span>}
      </label>
      {children}
      {sub && !error && <span style={{ fontSize: 11, color: "var(--color-text-muted)", fontStyle: "italic" }}>{sub}</span>}
      {error && <span style={{ fontSize: 11, color: "var(--color-danger)" }}>{error}</span>}
    </div>
  );
}
function Section({ title, children }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--color-text-muted)", borderBottom: "1px solid var(--color-border)", paddingBottom: 4 }}>{title}</div>
      {children}
    </div>
  );
}

/* Compact variety picker for a product row — dynamic droplist (base +
   any custom variety saved before) plus "Otra" free text, same pattern
   as CertificationRegistry.js's VarietyField but without the Field
   label wrapper since it lives inline in the products grid row. */
function VarietyField({ product, value, onChange, t }) {
  const options = getVarietyOptions(product);
  const isKnown = value && options.includes(value);
  const [mode, setMode] = useState(value && !isKnown && value !== "" ? "other" : "select");
  const [customVal, setCustomVal] = useState(mode === "other" ? value : "");
  useEffect(function () { setMode("select"); setCustomVal(""); }, [product]);
  const handleSelect = function (v) {
    if (v === "__other__") { setMode("other"); onChange(""); setCustomVal(""); }
    else { setMode("select"); onChange(v); }
  };
  const handleCustom = function (v) { setCustomVal(v); onChange(v); };
  const handleCustomBlur = function () { if (customVal.trim() && product) saveCustomVariety(product, customVal.trim()); };
  if (!product) return <SearchSelect disabled value="" onChange={function () {}} placeholder={t.selectVariety} options={[]} />;
  if (mode === "select") {
    return <SearchSelect value={value || ""} onChange={handleSelect} placeholder={t.selectVariety} options={options.map(function (v) { return { value: v, label: v }; }).concat([{ value: "__other__", label: t.otherVariety }])} />;
  }
  return (
    <div style={{ display: "flex", gap: 4 }}>
      <input type="text" value={customVal} onChange={function (e) { handleCustom(e.target.value); }} onBlur={handleCustomBlur} placeholder="..." style={{ ...inp, flex: 1 }} />
      <button type="button" onClick={function () { setMode("select"); onChange(""); }} style={{ padding: "4px 8px", borderRadius: 4, border: "1px solid var(--color-border)", background: "transparent", cursor: "pointer", fontSize: 11, color: "var(--color-text-muted)" }}>{"↩"}</button>
    </div>
  );
}

function requiredCertsFor(products, t) {
  const items = [
    { key: "safety", label: t.cert_safety },
    { key: "audit", label: t.cert_audit },
    { key: "corrective", label: t.cert_corrective },
    { key: "fsma", label: t.cert_fsma },
  ];
  const hasOrganic = (products || []).some(function (p) { return p.category === "Organic"; });
  /* 2026-09-28 (Arturo: "el auditing report en los organicos tampoco
     es obligatorio") — organicAudit dropped from the required list;
     status.organicAudit is still computed (below) in case anything
     else reads it, it just no longer counts toward the compliance
     semáforo. */
  if (hasOrganic) items.push({ key: "organic", label: t.cert_organic });
  return items;
}

/* ───────── Compliance evaluator (FIX 33/34/35) ─────────
   Each required cert key maps to a SPECIFIC document type that must
   be uploaded on a matching certification record. Just having a
   certification on file is NOT enough — the document with the right
   docType must be attached. If a grower has zero certs / zero docs
   the evaluator returns ALL items as missing → red semáforo.

   Mapping (docType strings come from
   CertificationRegistry's TypedDocumentSection):
     safety        → foodSafety + "Certificate"
     audit         → foodSafety + "Final Audit Report"
     corrective    → foodSafety + "Corrective Action Report"
     fsma          → foodSafety + "FSMA Summary Report"
     organic       → organic    + "Certificate"
     organicAudit  → organic    + "Auditing Report"

   Returns { level: 'green'|'orange'|'red', missing[], required[],
   total, satisfied, status: { [key]: bool } }. */
function evaluateGrowerCompliance(grower, certifications, t) {
  const required = requiredCertsFor(grower && grower.products, t);
  const growerCertId = "grower-" + ((grower && grower.id) || "");
  const ownCerts = (certifications || []).filter(function (c) {
    return c.entityId === growerCertId;
  });
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const active = function (c) {
    const end = c.anniversaryDate || c.validTo;
    if (!end) return true; // no expiry on file → treat as active
    return new Date(end) >= today;
  };
  /* True iff ANY cert of the right type, still active, carries an
     uploaded document whose docType matches. */
  const hasUploadedDoc = function (certType, docTypeName) {
    return ownCerts.some(function (c) {
      if (c.certType !== certType) return false;
      if (!active(c)) return false;
      const docs = Array.isArray(c.documents) ? c.documents : [];
      return docs.some(function (d) {
        if (!d || !d.docType) return false;
        return String(d.docType).toLowerCase() === String(docTypeName).toLowerCase();
      });
    });
  };
  /* 2026-09-28 (Arturo: "corrective actions... si el score de la
     audit inicial es 100%, corrective actions se desactiva como
     obligatoria") — same rule as CertificationRegistry.js's per-cert
     requiredDocs: an active foodSafety cert with a perfect prelimScore
     (the initial audit, before any correction) never had anything to
     correct, so it satisfies "corrective" on its own, no document
     needed. */
  const hasPerfectPrelimScore = ownCerts.some(function (c) {
    return c.certType === "foodSafety" && active(c) && parseFloat(c.prelimScore) === 100;
  });
  const status = {
    safety:       hasUploadedDoc("foodSafety", "Certificate"),
    audit:        hasUploadedDoc("foodSafety", "Final Audit Report"),
    corrective:   hasUploadedDoc("foodSafety", "Corrective Action Report") || hasPerfectPrelimScore,
    fsma:         hasUploadedDoc("foodSafety", "FSMA Summary Report"),
    organic:      hasUploadedDoc("organic",    "Certificate"),
    organicAudit: hasUploadedDoc("organic",    "Auditing Report"),
  };
  const missing = required.filter(function (item) { return !status[item.key]; });
  const satisfied = required.length - missing.length;
  let level;
  if (required.length === 0) level = "red";
  else if (missing.length === 0) level = "green";
  else if (satisfied === 0) level = "red";
  else level = "orange";
  return { level: level, missing: missing.map(function (m) { return m.label; }), required: required, status: status, total: required.length, satisfied: satisfied };
}

/* Has at least one Submitted / Accepted shipment for the grower's
   active season? Used by FIX 21 to switch the delete button to a
   deactivate when work has started for the season. */
function growerHasActiveSeasonShipments(grower, shipments) {
  if (!grower || !grower.seasonYearFrom) return false;
  const yr = String(grower.seasonYearFrom);
  return (shipments || []).some(function (s) {
    const st = s.shipperStatus;
    if (st !== "submitted" && st !== "accepted") return false;
    const d = s.dateDeparture || s.dateArrival || "";
    return d.startsWith(yr);
  });
}

function dialToCountryCode(d) {
  var withPlus = "+" + String(d || "").replace(/\D/g, "");
  var hit = PHONE_COUNTRIES.find(function (c) { return c.dial === withPlus; });
  return hit ? hit.code : "MX";
}
function countryCodeToDialDigits(code) {
  var hit = PHONE_COUNTRIES.find(function (c) { return c.code === code; });
  return hit ? hit.dial.replace(/\D/g, "") : "52";
}

/* 2026-09-30 (Arturo: "LOS GROWERS AL ENTRAR AL MODULO DEBE TENER UN
   BOTON DE EDITAR, ACTUALMEMNTE ENTRO Y ESTA EN MODO EDITABLE
   AUTOMATICAMENTE") — REGLA DE VENTANAS violation: clicking a row went
   straight into the editable GrowerForm, with no view-mode gate. Same
   fix as every other registry in the app (WarehouseRegistry.js,
   ExportCertificateRegistry.js, etc.): a read-only DetailPanel opens
   first (DraggableModal), with a real "Edit" button that swaps to
   GrowerForm — only one window ever visible at a time, never a
   default-editable landing. */
function DetailPanel({ grower, t, lang, certifications, canEdit, onEdit, onClose }) {
  const g = grower;
  const phone = [g.phoneCountry, g.phoneArea, g.phoneNumber].filter(Boolean).length > 0
    ? ("+" + (g.phoneCountry || "52") + " " + (g.phoneArea || "") + " " + (g.phoneNumber || "")).trim()
    : "";
  const cpl = evaluateGrowerCompliance(g, certifications || [], t);
  const dotColor = cpl.level === "green" ? "#059669" : cpl.level === "orange" ? "#F59E0B" : "#DC2626";
  const dotBg = cpl.level === "green" ? "rgba(16,185,129,0.13)" : cpl.level === "orange" ? "rgba(245,158,11,0.13)" : "rgba(220,38,38,0.13)";
  const curStatus = g.status || (g.isActive === false ? "archived" : "active");
  const statusLabel = curStatus === "active" ? t.status_active : curStatus === "paused" ? t.status_paused : t.status_archived;
  const statusColor = curStatus === "active" ? "#059669" : curStatus === "paused" ? "#D97706" : "#6B7280";
  const ranchoAddr = function (r) {
    return [r.street || r.address, r.municipio, r.city, r.state, r.zip, r.country].filter(Boolean).join(", ");
  };
  return (
    <DraggableModal title={getFullName(g)} onClose={onClose} initialWidth={620} initialHeight={620}>
      <div style={{ padding: "12px 22px", borderBottom: "1px solid var(--color-border)", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <span style={{ padding: "3px 10px", borderRadius: 12, fontSize: 10, fontWeight: 700, background: "rgba(0,0,0,0.06)", color: statusColor }}>{statusLabel}</span>
          <span title={cpl.level === "green" ? "" : (lang === "es" ? "Faltan: " : "Missing: ") + cpl.missing.join(", ")} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "3px 9px", borderRadius: 10, fontSize: 10, fontWeight: 700, background: dotBg, color: dotColor }}>
            <span style={{ width: 8, height: 8, borderRadius: 4, background: dotColor }} />
            {cpl.satisfied}/{cpl.total || 0} {t.columns.compliance}
          </span>
        </div>
        {canEdit && <button onClick={function () { onEdit(g); }} style={{ padding: "7px 16px", borderRadius: 6, border: "none", background: "var(--color-accent)", color: "var(--color-accent-text)", cursor: "pointer", fontSize: 12, fontWeight: 700 }}>{t.editBtn}</button>}
      </div>
      <div style={{ flex: 1, overflowY: "auto", padding: "18px 22px", display: "flex", flexDirection: "column", gap: 18 }}>
        <Section title={t.section_identity}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px 20px", fontSize: 13 }}>
            <div><div style={{ fontSize: 10, color: "var(--color-text-muted)" }}>{t.producerCode}</div><div style={{ fontWeight: 600, fontFamily: "'JetBrains Mono', monospace" }}>{g.producerCode || "—"}</div></div>
            <div><div style={{ fontSize: 10, color: "var(--color-text-muted)" }}>{t.section_season}</div><div style={{ fontWeight: 600 }}>{seasonLabel(g)}</div></div>
          </div>
        </Section>
        <Section title={t.section_fiscal}>
          <div style={{ fontSize: 13 }}>{ranchoAddr(g.domicilioFiscal || {}) || "—"}</div>
        </Section>
        <Section title={t.section_contact}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px 20px", fontSize: 13 }}>
            <div><div style={{ fontSize: 10, color: "var(--color-text-muted)" }}>{t.phone}</div><div>{phone || "—"}</div></div>
            <div><div style={{ fontSize: 10, color: "var(--color-text-muted)" }}>Email</div><div>{g.email || "—"}</div></div>
          </div>
        </Section>
        <Section title={`${t.section_products} (${(g.products || []).length})`}>
          {(g.products || []).length === 0 ? (
            <div style={{ fontSize: 12, color: "var(--color-text-muted)", fontStyle: "italic" }}>—</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {(g.products || []).map(function (p, i) {
                return (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, padding: "6px 10px", background: "var(--color-bg)", borderRadius: 6, border: "1px solid var(--color-border)" }}>
                    <strong>{p.product || "—"}</strong>
                    {p.variety && <span style={{ color: "var(--color-text-secondary)" }}>{p.variety}</span>}
                    <span style={{ marginLeft: "auto", fontSize: 10, fontWeight: 700, padding: "1px 6px", borderRadius: 4, background: p.category === "Conventional" ? "var(--color-badge-cv-bg)" : "var(--color-badge-og-bg)", color: p.category === "Conventional" ? "var(--color-badge-cv-text)" : "var(--color-badge-og-text)" }}>{p.category === "Conventional" ? "CV" : "OG"}</span>
                  </div>
                );
              })}
            </div>
          )}
        </Section>
        <Section title={`${t.section_ranchos} (${(g.ranchos || []).length})`}>
          {(g.ranchos || []).length === 0 ? (
            <div style={{ fontSize: 12, color: "var(--color-text-muted)", fontStyle: "italic" }}>—</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {(g.ranchos || []).map(function (r, i) {
                return (
                  <div key={i} style={{ fontSize: 13, padding: "8px 10px", background: "var(--color-bg)", borderRadius: 6, border: "1px solid var(--color-border)" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <strong>{r.name || "—"}</strong>
                      {r.hectares && <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: "var(--color-text-muted)" }}>{r.hectares} ha</span>}
                    </div>
                    <div style={{ fontSize: 11, color: "var(--color-text-secondary)", marginTop: 2 }}>{ranchoAddr(r) || "—"}</div>
                  </div>
                );
              })}
            </div>
          )}
        </Section>
      </div>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, padding: "12px 22px", borderTop: "1px solid var(--color-border)" }}>
        <button onClick={onClose} style={{ padding: "8px 16px", borderRadius: 6, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 13, fontWeight: 500 }}>{t.close}</button>
      </div>
    </DraggableModal>
  );
}

function GrowerForm({ grower, t, lang, supplierId, supplier, locked, certifications, canDelete, onSave, onCancel, onDelete, onRequestChange }) {
  const isEdit = !!grower;
  const [f, setF] = useState(function () {
    var base = Object.assign({}, EMPTY, grower || {});
    /* Legacy growers predate this field, or may only have part of it
       saved — merge onto EMPTY_FISCAL so every sub-field is always a
       defined string (never undefined, which would make the input
       uncontrolled and throw a React warning the first time it's
       typed into). */
    base.domicilioFiscal = Object.assign({}, EMPTY_FISCAL, (grower && grower.domicilioFiscal) || {});
    return base;
  });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [errBanner, setErrBanner] = useState("");
  const set = function (k, v) { setF(function (p) { return Object.assign({}, p, { [k]: v }); }); };

  const isFisica = f.personType !== "moral";
  const productOpts = [
    { value: "Blackberry", label: t.blackberry },
    { value: "Blueberry",  label: t.blueberry },
    { value: "Raspberry",  label: t.raspberry },
  ];

  /* Auto-link the year end as `from + 1` whenever the user types in
     the start year. Keeps the "2025-2026" invariant the spec demands
     without making the user double-type. */
  const onYearFrom = function (v) {
    const yr = (v || "").replace(/\D/g, "").slice(0, 4);
    set("seasonYearFrom", yr);
    if (yr.length === 4) set("seasonYearTo", String(parseInt(yr, 10) + 1));
  };

  const validate = function () {
    const e = {};
    if (isFisica) {
      if (!f.nombre.trim()) e.nombre = t.req;
      if (!f.apPaterno.trim()) e.apPaterno = t.req;
    } else {
      if (!f.razonSocial.trim()) e.razonSocial = t.req;
    }
    /* 2026-09-28 (Arturo: "el campo codigo no debe ser obligatorio") —
       no longer required; still validated as exactly 3 digits
       (001-999), no 000, whenever the user does fill it in. */
    var pc = (f.producerCode || "").trim();
    if (pc && (!/^\d{3}$/.test(pc) || pc === "000")) e.producerCode = t.req;
    if (!(f.domicilioFiscal && f.domicilioFiscal.street && f.domicilioFiscal.street.trim())) e.fiscal = t.fiscalReq;
    /* 2026-09-29 (Arturo: "solo quiero que el productor este siempre
       activo sin importar la temporada") — the grower is a persistent
       entity (certificates link to it by a stable id, same as a
       Supplier/Client), not something that needs re-registering every
       season. Season years are now optional — only validated for
       internal consistency (end = start + 1) when at least one of
       them is actually filled in; a grower with neither is perfectly
       valid and stays active indefinitely. */
    const yf = parseInt(f.seasonYearFrom, 10);
    const yt = parseInt(f.seasonYearTo, 10);
    if ((f.seasonYearFrom || f.seasonYearTo) && (!yf || !yt || yt !== yf + 1)) e.season = t.seasonInvalid;
    if (!f.products || f.products.length === 0 || f.products.some(function (p) { return !p.product; })) {
      e.products = t.productReq;
    }
    /* At least one rancho with name + street (or legacy address). FIX 18. */
    if (!f.ranchos || f.ranchos.length === 0) {
      e.ranchos = t.ranchoReq;
    } else if (f.ranchos.some(function (r) {
      var hasStreet = (r.street || r.address || "").trim();
      return !(r.name || "").trim() || !hasStreet;
    })) {
      e.ranchos = t.ranchoReq;
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const save = async function () {
    setErrBanner("");
    if (!validate()) return;
    setBusy(true);
    try {
      const rec = Object.assign({}, f, {
        id: f.id || ("GRW-" + Date.now()),
        shipperId: supplierId,
      });
      await saveToSupabase("growers", rec);
      onSave(rec);
    } catch (e) {
      setErrBanner((e && e.message) || String(e));
    } finally { setBusy(false); }
  };

  const addProduct = function () { setF(function (p) { return Object.assign({}, p, { products: (p.products || []).concat([Object.assign({}, EMPTY_PRODUCT)]) }); }); };
  const setProduct = function (idx, k, v) {
    setF(function (p) {
      const next = (p.products || []).slice();
      next[idx] = Object.assign({}, next[idx], { [k]: v });
      return Object.assign({}, p, { products: next });
    });
  };
  const rmProduct = function (idx) {
    setF(function (p) {
      const next = (p.products || []).slice();
      next.splice(idx, 1);
      return Object.assign({}, p, { products: next });
    });
  };

  /* 2026-10-26, Arturo — ranchos now carry a stable `id` so the
     Export Certificate form (ExportCertificateRegistry.js) can
     reference specific ranchos by id in its own multi-select, not by
     array position (which shifts if one is removed). Same
     Date.now()+Math.random() id pattern already used elsewhere in
     this app for multi-add rows. */
  const addRancho = function () { setF(function (p) { return Object.assign({}, p, { ranchos: (p.ranchos || []).concat([Object.assign({}, EMPTY_RANCHO, { id: "RCH-" + Date.now() + "-" + Math.round(Math.random() * 1e6) })]) }); }); };
  const setRancho = function (idx, k, v) {
    setF(function (p) {
      const next = (p.ranchos || []).slice();
      next[idx] = Object.assign({}, next[idx], { [k]: v });
      return Object.assign({}, p, { ranchos: next });
    });
  };
  const rmRancho = function (idx) {
    setF(function (p) {
      const next = (p.ranchos || []).slice();
      next.splice(idx, 1);
      return Object.assign({}, p, { ranchos: next });
    });
  };
  const setFiscal = function (k, v) {
    setF(function (p) { return Object.assign({}, p, { domicilioFiscal: Object.assign({}, p.domicilioFiscal, { [k]: v }) }); });
  };
  /* "pueda copiar del domicilio fiscal si fuese el mismo o ingresar
     uno distinto" — one-time copy, not a live sync: after copying,
     the rancho's address fields are perfectly normal inputs the user
     can still edit independently (e.g. the 2nd rancho that does NOT
     match the fiscal address). */
  const copyFiscalToRancho = function (idx) {
    setF(function (p) {
      const next = (p.ranchos || []).slice();
      next[idx] = Object.assign({}, next[idx], {
        street: p.domicilioFiscal.street, municipio: p.domicilioFiscal.municipio, city: p.domicilioFiscal.city,
        state: p.domicilioFiscal.state, zip: p.domicilioFiscal.zip, country: p.domicilioFiscal.country,
      });
      return Object.assign({}, p, { ranchos: next });
    });
  };

  const reqCerts = requiredCertsFor(f.products, t);
  const readonly = locked && isEdit;

  return (
    <DraggableModal title={isEdit ? t.form_edit : t.form_new} onClose={busy ? function () {} : onCancel} initialWidth={780} initialHeight={Math.min(800, window.innerHeight - 60)}>
      <div style={{ overflow: "auto", flex: 1, padding: "18px 22px" }}>
        {readonly && (
          <div style={{ background: "rgba(245,158,11,0.08)", border: "1px solid rgba(245,158,11,0.25)", borderRadius: 6, padding: "10px 12px", fontSize: 12, color: "#B45309", fontWeight: 500, marginBottom: 14, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
            <span>🔒 {t.locked}</span>
            <button onClick={onRequestChange} style={{ padding: "6px 12px", borderRadius: 5, border: "1px solid #B45309", background: "transparent", color: "#B45309", cursor: "pointer", fontSize: 11, fontWeight: 700 }}>{t.requestChange}</button>
          </div>
        )}
        {errBanner && <div style={{ background: "rgba(231,76,60,0.10)", border: "1px solid rgba(231,76,60,0.25)", borderRadius: 6, padding: "8px 12px", fontSize: 12, color: "var(--color-danger)", fontWeight: 500, marginBottom: 14 }}>{errBanner}</div>}

        <fieldset disabled={readonly} style={{ border: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 18 }}>
          <Section title={t.section_identity}>
            <Field label={t.personType} required>
              <div style={{ display: "flex", border: "1px solid var(--color-border)", borderRadius: 6, overflow: "hidden" }}>
                {[{ k: "fisica", l: t.individual }, { k: "moral", l: t.business }].map(function (o) {
                  const on = (f.personType === o.k) || (o.k === "fisica" && f.personType !== "moral");
                  return (
                    <button key={o.k} type="button" onClick={function () { set("personType", o.k); }}
                      style={{ flex: 1, padding: "9px 12px", border: "none", background: on ? "var(--color-accent)" : "transparent", color: on ? "var(--color-accent-text)" : "var(--color-text-muted)", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>{o.l}</button>
                  );
                })}
              </div>
            </Field>
            {isFisica ? (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
                <Field label={t.nombre} required error={errors.nombre}>
                  <input value={f.nombre} onChange={function (e) { set("nombre", e.target.value); }} style={Object.assign({}, inp, errors.nombre ? errS : {})} />
                </Field>
                <Field label={t.apPaterno} required error={errors.apPaterno}>
                  <input value={f.apPaterno} onChange={function (e) { set("apPaterno", e.target.value); }} style={Object.assign({}, inp, errors.apPaterno ? errS : {})} />
                </Field>
                <Field label={t.apMaterno}>
                  <input value={f.apMaterno} onChange={function (e) { set("apMaterno", e.target.value); }} style={inp} />
                </Field>
              </div>
            ) : (
              <Field label={t.razonSocial} required error={errors.razonSocial}>
                <input value={f.razonSocial} onChange={function (e) { set("razonSocial", e.target.value); }} style={Object.assign({}, inp, errors.razonSocial ? errS : {})} />
              </Field>
            )}
          </Section>

          <Section title={t.section_fiscal}>
            {/* 2026-10-26, Arturo: "necesito un domicilio fiscal" —
                same structured shape as a rancho address, but
                belongs to the persona física/moral itself, not to
                any single rancho (a grower can have 1 fiscal address
                and 2+ ranchos with different addresses). */}
            <Field label={t.fiscalStreet} required error={errors.fiscal}>
              <input value={f.domicilioFiscal.street} onChange={function (e) { setFiscal("street", e.target.value); }} style={Object.assign({}, inp, errors.fiscal ? errS : {})} />
            </Field>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <Field label={t.fiscalMunicipio}>
                <input value={f.domicilioFiscal.municipio} onChange={function (e) { setFiscal("municipio", e.target.value); }} style={inp} />
              </Field>
              <Field label={t.fiscalCity}>
                <input value={f.domicilioFiscal.city} onChange={function (e) { setFiscal("city", e.target.value); }} style={inp} />
              </Field>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 120px 1fr", gap: 10 }}>
              <Field label={t.fiscalState}>
                <input value={f.domicilioFiscal.state} onChange={function (e) { setFiscal("state", e.target.value); }} style={inp} />
              </Field>
              <Field label={t.fiscalZip}>
                <input value={f.domicilioFiscal.zip} onChange={function (e) { setFiscal("zip", e.target.value); }} style={Object.assign({}, inp, { fontFamily: "'JetBrains Mono', monospace", textAlign: "center" })} />
              </Field>
              <Field label={t.fiscalCountry}>
                <input value={f.domicilioFiscal.country} onChange={function (e) { setFiscal("country", e.target.value); }} style={inp} />
              </Field>
            </div>
          </Section>

          <Section title={t.section_contact}>
            {/* Both fields optional per FIX 18. */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <Field label={t.ranchoPhone}>
                <PhoneField
                  value={serializePhone(dialToCountryCode(f.phoneCountry || "52"), f.phoneArea, f.phoneNumber)}
                  onChange={function (s) {
                    var p = parsePhoneShared(s);
                    set("phoneCountry", countryCodeToDialDigits(p.country));
                    set("phoneArea", p.area);
                    set("phoneNumber", p.local);
                  }}
                  lang={lang}
                  inputStyle={inp}
                />
              </Field>
              <Field label={t.ranchoEmail}>
                <input type="email" value={f.email || ""} onChange={function (e) { set("email", e.target.value); }} style={inp} />
              </Field>
            </div>
          </Section>

          <Section title={t.section_code}>
            <Field label={t.producerCode} sub={t.producerCodeSub} error={errors.producerCode}>
              {/* Exactly 3 digits, 001-999. Numbers only — letters and
                  leading 000 are rejected on validate(). */}
              <input
                value={f.producerCode}
                onChange={function (e) {
                  var digits = (e.target.value || "").replace(/\D/g, "").slice(0, 3);
                  set("producerCode", digits);
                }}
                inputMode="numeric"
                maxLength={3}
                placeholder="001"
                style={Object.assign({}, inp, { width: 110, fontFamily: "'JetBrains Mono', monospace", fontWeight: 600, textAlign: "center", letterSpacing: "0.1em" }, errors.producerCode ? errS : {})}
              />
            </Field>
          </Section>

          <Section title={t.section_season}>
            {/* 4-digit numeric only, narrow width — see FIX 14. */}
            <div style={{ display: "flex", gap: 10, alignItems: "flex-end" }}>
              <Field label={t.yearFrom} error={errors.season}>
                <input value={f.seasonYearFrom} onChange={function (e) { onYearFrom(e.target.value); }} maxLength={4} inputMode="numeric"
                  style={Object.assign({}, inp, { width: 90, fontFamily: "'JetBrains Mono', monospace", textAlign: "center", letterSpacing: "0.1em" }, errors.season ? errS : {})} />
              </Field>
              <span style={{ fontSize: 16, color: "var(--color-text-muted)", paddingBottom: 8 }}>–</span>
              <Field label={t.yearTo}>
                <input value={f.seasonYearTo} onChange={function (e) { set("seasonYearTo", (e.target.value || "").replace(/\D/g, "").slice(0, 4)); }} maxLength={4} inputMode="numeric"
                  style={Object.assign({}, inp, { width: 90, fontFamily: "'JetBrains Mono', monospace", textAlign: "center", letterSpacing: "0.1em" }, errors.season ? errS : {})} />
              </Field>
            </div>
            <div style={{ fontSize: 11, color: "var(--color-text-muted)", fontStyle: "italic" }}>{t.seasonHint}</div>
          </Section>

          <Section title={t.section_products}>
            {/* FIX 33 — per-product compliance reads from the
                certifications table. OG products need all 6 docs;
                CV products need the 4 food-safety docs. The chip
                flips to OK only when every required doc for that
                row's category is uploaded. */}
            {(function () {
              var liveCompliance = evaluateGrowerCompliance(Object.assign({}, grower || {}, { id: (grower && grower.id) || f.id, products: f.products }), certifications, t);
              var st = liveCompliance.status;
              var cvOk = st.safety && st.audit && st.corrective && st.fsma;
              var ogOk = cvOk && st.organic;
              return (f.products || []).map(function (p, idx) {
                var rowOk = p.category === "Organic" ? ogOk : cvOk;
                return (
                  <div key={idx} style={{ display: "grid", gridTemplateColumns: "1fr 150px 1fr 110px 36px", gap: 8, alignItems: "center" }}>
                    <SearchSelect value={p.product} onChange={function (v) { setProduct(idx, "product", v); setProduct(idx, "variety", ""); }} options={productOpts} placeholder={t.pickProduct} />
                    <SearchSelect value={p.category} onChange={function (v) { setProduct(idx, "category", v); }} options={[{ value: "Organic", label: t.organic }, { value: "Conventional", label: t.conventional }]} />
                    <VarietyField product={p.product} value={p.variety || ""} onChange={function (v) { setProduct(idx, "variety", v); }} t={t} />
                    <span style={{
                      padding: "4px 8px", borderRadius: 8, fontSize: 10, fontWeight: 700, textAlign: "center",
                      background: rowOk ? "rgba(16,185,129,0.13)" : "rgba(220,38,38,0.13)",
                      color:      rowOk ? "#059669" : "#DC2626",
                    }}>{rowOk ? "✓ " + t.compliance_ok : "● " + t.compliance_pending}</span>
                    <button type="button" onClick={function () { rmProduct(idx); }} style={{ background: "transparent", border: "none", color: "var(--color-danger)", cursor: "pointer", fontSize: 18, padding: 4 }}>×</button>
                  </div>
                );
              });
            })()}
            <button type="button" onClick={addProduct} style={{ padding: "8px 14px", borderRadius: 6, border: "1px dashed var(--color-border)", background: "transparent", color: "var(--color-accent)", cursor: "pointer", fontSize: 12, fontWeight: 600 }}>{t.addProduct}</button>
            {errors.products && <span style={{ fontSize: 11, color: "var(--color-danger)" }}>{errors.products}</span>}
          </Section>

          <Section title={t.section_certs}>
            {/* FIX 19 — each required cert lights GREEN when the grower
                has the matching certification on file (food-safety
                cluster vs organic cluster) and RED when missing. */}
            {(function () {
              var liveCompliance = evaluateGrowerCompliance(Object.assign({}, grower || {}, { id: (grower && grower.id) || f.id, products: f.products }), certifications, t);
              return (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, fontSize: 12 }}>
                  {reqCerts.map(function (c) {
                    var ok = liveCompliance.status[c.key];
                    return (
                      <div key={c.key} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", background: ok ? "rgba(16,185,129,0.06)" : "rgba(220,38,38,0.06)", borderRadius: 6, border: "1px solid " + (ok ? "rgba(16,185,129,0.25)" : "rgba(220,38,38,0.25)") }}>
                        <span style={{ fontSize: 14, color: ok ? "#059669" : "#DC2626" }}>{ok ? "✓" : "●"}</span>
                        <span style={{ color: "var(--color-text)", fontWeight: 500, flex: 1 }}>{c.label}</span>
                        {!ok && <span style={{ fontSize: 9, fontWeight: 700, color: "#DC2626", textTransform: "uppercase", letterSpacing: "0.05em" }}>{lang === "es" ? "Falta" : "Missing"}</span>}
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </Section>

          <Section title={t.section_ranchos}>
            {(f.ranchos || []).map(function (r, idx) {
              return (
                <div key={idx} style={{ background: "var(--color-bg)", borderRadius: 8, padding: 12, border: "1px solid var(--color-border)", display: "flex", flexDirection: "column", gap: 8, position: "relative" }}>
                  <button type="button" onClick={function () { rmRancho(idx); }} style={{ position: "absolute", top: 6, right: 8, background: "transparent", border: "none", color: "var(--color-danger)", cursor: "pointer", fontSize: 18, padding: 2 }}>×</button>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 110px", gap: 8 }}>
                    <input placeholder={t.ranchoName} value={r.name || ""} onChange={function (e) { setRancho(idx, "name", e.target.value); }} style={inp} />
                    <input type="number" min="0" step="0.01" placeholder={t.ranchoHectares} value={r.hectares || ""} onChange={function (e) { setRancho(idx, "hectares", e.target.value); }} style={Object.assign({}, inp, { fontFamily: "'JetBrains Mono', monospace", textAlign: "right" })} />
                  </div>
                  {/* 2026-10-26, Arturo: "al agregar el domicilio de
                      los ranchos pueda copiar del domicilio fiscal si
                      fuese el mismo o ingresar uno distinto" —
                      one-time copy, never forced; the fields below
                      stay perfectly normal inputs either way. */}
                  <div style={{ display: "flex", justifyContent: "flex-end" }}>
                    <button type="button" onClick={function () { copyFiscalToRancho(idx); }} style={{ padding: "3px 10px", borderRadius: 5, border: "1px solid var(--color-accent)", background: "transparent", color: "var(--color-accent)", cursor: "pointer", fontSize: 11, fontWeight: 600 }}>{t.copyFiscalToRancho}</button>
                  </div>
                  <input placeholder={t.ranchoStreet} value={r.street || r.address || ""} onChange={function (e) { setRancho(idx, "street", e.target.value); }} style={inp} />
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                    <input placeholder={t.ranchoMunicipio} value={r.municipio || ""} onChange={function (e) { setRancho(idx, "municipio", e.target.value); }} style={inp} />
                    <input placeholder={t.ranchoCity} value={r.city || ""} onChange={function (e) { setRancho(idx, "city", e.target.value); }} style={inp} />
                  </div>
                  {/* FIX 32 — Estado / State separated from the rest of the address. */}
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 120px 1fr", gap: 8 }}>
                    <input placeholder={t.ranchoState} value={r.state || ""} onChange={function (e) { setRancho(idx, "state", e.target.value); }} style={inp} />
                    <input placeholder={t.ranchoZip} value={r.zip || ""} onChange={function (e) { setRancho(idx, "zip", e.target.value); }} style={Object.assign({}, inp, { fontFamily: "'JetBrains Mono', monospace", textAlign: "center" })} />
                    <input placeholder={t.ranchoCountry} value={r.country || ""} onChange={function (e) { setRancho(idx, "country", e.target.value); }} style={inp} />
                  </div>
                  <textarea placeholder={t.ranchoNotes} value={r.notes || ""} onChange={function (e) { setRancho(idx, "notes", e.target.value); }} rows={2} style={Object.assign({}, inp, { resize: "vertical", fontFamily: "inherit" })} />
                </div>
              );
            })}
            <button type="button" onClick={addRancho} style={{ padding: "8px 14px", borderRadius: 6, border: "1px dashed var(--color-border)", background: "transparent", color: "var(--color-accent)", cursor: "pointer", fontSize: 12, fontWeight: 600 }}>{t.addRancho}</button>
            {errors.ranchos && <span style={{ fontSize: 11, color: "var(--color-danger)" }}>{errors.ranchos}</span>}
          </Section>
        </fieldset>
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", gap: 8, padding: "12px 22px", borderTop: "1px solid var(--color-border)", background: "var(--color-bg)" }}>
        {/* FIX 21 — Delete becomes Deactivate once the grower's
            season has at least one Submitted/Accepted shipment.
            `canDelete` = no work yet, the row can be erased. */}
        <div>
          {isEdit && onDelete && !readonly && (
            canDelete ? (
              <button onClick={onDelete} disabled={busy} style={{ padding: "8px 14px", borderRadius: 6, border: "1px solid rgba(220,38,38,0.4)", background: "rgba(220,38,38,0.06)", color: "#DC2626", cursor: busy ? "not-allowed" : "pointer", fontSize: 12, fontWeight: 600 }}>{t.delete_full || (lang === "es" ? "Eliminar" : "Delete")}</button>
            ) : (
              <button onClick={onDelete} disabled={busy} title={lang === "es" ? "Hay embarques de la temporada activa — solo se puede desactivar" : "Active-season shipments exist — only deactivate is allowed"} style={{ padding: "8px 14px", borderRadius: 6, border: "1px solid rgba(107,114,128,0.4)", background: "var(--color-bg-card)", color: "var(--color-text-secondary)", cursor: busy ? "not-allowed" : "pointer", fontSize: 12, fontWeight: 600 }}>{lang === "es" ? "Desactivar" : "Deactivate"}</button>
            )
          )}
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {/* FIX 39 — Audit Report (Preview / Print PDF / Excel).
              Available only on saved growers (isEdit). The buttons
              open a styled report window or trigger an .xlsx download
              built from grower + certifications + supplier. */}
          {isEdit && (
            <>
              <button type="button" onClick={function () { openGrowerAuditPreview(f, { certifications: certifications, supplier: supplier, lang: lang }, { print: false }); }} title={lang === "es" ? "Vista previa del reporte" : "Audit report preview"} style={{ padding: "8px 12px", borderRadius: 6, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 11, fontWeight: 600 }}>{lang === "es" ? "👁 Vista Previa" : "👁 Preview"}</button>
              <button type="button" onClick={function () { downloadGrowerAuditPdf(f, { certifications: certifications, supplier: supplier, lang: lang }); }} title={lang === "es" ? "Descargar PDF" : "Download PDF"} style={{ padding: "8px 12px", borderRadius: 6, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 11, fontWeight: 600 }}>{lang === "es" ? "🖨 PDF" : "🖨 PDF"}</button>
              <button type="button" onClick={function () { downloadGrowerAuditXLSX(f, { certifications: certifications, supplier: supplier, lang: lang }); }} title={lang === "es" ? "Exportar a Excel" : "Export to Excel"} style={{ padding: "8px 12px", borderRadius: 6, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 11, fontWeight: 600 }}>{lang === "es" ? "📊 Excel" : "📊 Excel"}</button>
              <span style={{ width: 1, height: 22, background: "var(--color-border)", margin: "0 4px" }} />
            </>
          )}
          <button onClick={onCancel} disabled={busy} style={{ padding: "8px 14px", borderRadius: 6, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: busy ? "not-allowed" : "pointer", fontSize: 12, fontWeight: 600 }}>{readonly ? t.close : t.cancel}</button>
          {!readonly && (
            <button onClick={save} disabled={busy} style={{ padding: "8px 16px", borderRadius: 6, border: "none", background: "var(--color-accent)", color: "var(--color-accent-text)", cursor: busy ? "not-allowed" : "pointer", fontSize: 12, fontWeight: 700, opacity: busy ? 0.7 : 1 }}>{busy ? t.saving : t.save}</button>
          )}
        </div>
      </div>
    </DraggableModal>
  );
}

function RequestChangeModal({ grower, t, lang, supplierId, profile, onSent, onClose }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async function () {
    if (!text.trim()) return;
    setBusy(true);
    try {
      const entry = {
        id: "CHGR-" + Date.now(),
        message: text.trim(),
        requestedAt: new Date().toISOString(),
        requestedBy: (profile && profile.email) || "",
        status: "open",
      };
      const next = Object.assign({}, grower, {
        changeRequests: (grower.changeRequests || []).concat([entry]),
      });
      await saveToSupabase("growers", next);
      onSent(next);
    } catch (e) { /* ignore */ }
    finally { setBusy(false); }
  };
  return (
    <DraggableModal title={t.requestChangeTitle} onClose={onClose} initialWidth={520} initialHeight={360}>
      <div style={{ padding: "18px 22px", flex: 1, display: "flex", flexDirection: "column", gap: 12 }}>
        <p style={{ margin: 0, fontSize: 12, color: "var(--color-text-muted)" }}>{t.requestChangeSub}</p>
        <textarea value={text} onChange={function (e) { setText(e.target.value); }} rows={6} style={Object.assign({}, inp, { resize: "vertical", fontFamily: "inherit", flex: 1 })} />
      </div>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, padding: "12px 22px", borderTop: "1px solid var(--color-border)", background: "var(--color-bg)" }}>
        <button onClick={onClose} style={{ padding: "8px 14px", borderRadius: 6, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 12, fontWeight: 600 }}>{t.cancel}</button>
        <button onClick={send} disabled={busy || !text.trim()} style={{ padding: "8px 16px", borderRadius: 6, border: "none", background: "var(--color-accent)", color: "var(--color-accent-text)", cursor: busy ? "not-allowed" : "pointer", fontSize: 12, fontWeight: 700, opacity: busy ? 0.7 : 1 }}>{busy ? t.saving : t.save}</button>
      </div>
    </DraggableModal>
  );
}

export default function ShipperGrowers({ supplierId, supplier, profile, lang }) {
  const t = T[lang === "en" ? "en" : "es"];
  /* Permission flags. When `profile` is null we're on the admin path
     (GrowerRegistry imports this component directly) — admin always
     gets full access, so default to true rather than running through
     hasPermission (which returns false when profile is null). */
  const canCreate = profile ? hasPermission(profile, "create_growers") : true;
  const canEdit   = profile ? hasPermission(profile, "edit_growers")   : true;
  const canDelete = profile ? hasPermission(profile, "delete_growers") : true;
  const [rows, setRows] = useState([]);
  const [shipments, setShipments] = useState([]);
  const [certifications, setCertifications] = useState([]);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [requesting, setRequesting] = useState(null);
  const [loading, setLoading] = useState(true);
  /* FIX 58 — mass-action selection. Keys are grower ids; the value
     true means the row is checked. Cleared whenever the filter
     narrows the visible set so the count badge stays honest. */
  const [selected, setSelected] = useState({});

  const load = useCallback(function () {
    setLoading(true);
    Promise.all([
      loadFromSupabase("growers", { force: true }).catch(function () { return []; }),
      loadFromSupabase("shipments").catch(function () { return []; }),
      /* Certifications feed the compliance semáforo + the per-cert
         RED/GREEN status in the form. */
      loadFromSupabase("certifications").catch(function () { return []; }),
    ]).then(function (res) {
      var myGrowers = (res[0] || []).filter(function (g) { return String(g.shipperId) === String(supplierId); });
      setRows(myGrowers);
      setShipments((res[1] || []).filter(function (s) { return String(s.supplierId) === String(supplierId); }));
      /* FIX 64 — Tenancy guard. Cert.supplierId in production data
         carries the supplier UUID, but `supplierId` here is the
         legacy id from ShipperPortalShell — accept matches against
         EITHER form. Loads the suppliers list to resolve our uuid
         once. */
      loadFromSupabase("suppliers").catch(function () { return []; }).then(function (sups) {
        var mySupplier = (sups || []).find(function (s) { return String(s.id) === String(supplierId); });
        var myUuid = mySupplier && mySupplier._uuid ? String(mySupplier._uuid) : "";
        var myGrowerShipperIds = {};
        myGrowers.forEach(function (g) { myGrowerShipperIds[String(g.id)] = true; });
        /* effectiveSupplierId returns whatever form the cert carries.
           Caller matches against both myUuid and supplierId. */
        var effectiveSupplierId = function (c) {
          if (!c) return "";
          var sid = c.supplierId;
          if ((sid == null || sid === "") && c.extra && typeof c.extra === "object") {
            sid = c.extra.supplierId;
          }
          if (sid != null && sid !== "") return String(sid);
          var eid = String(c.entityId || "");
          /* Pattern-match on entityId, NOT entityType — legacy rows
             often shipped with entityType blank but entityId
             populated. */
          if (eid.indexOf("shipper-") === 0) return eid.replace("shipper-", "");
          if (eid.indexOf("grower-") === 0) {
            var gid = eid.replace("grower-", "");
            return myGrowerShipperIds[gid] ? String(supplierId) : "";
          }
          return "";
        };
        var myCerts = (res[2] || []).filter(function (c) {
          var certSid = effectiveSupplierId(c);
          if (certSid === String(supplierId)) return true;
          if (myUuid && certSid === myUuid) return true;
          return false;
        });
        setCertifications(myCerts);
        setLoading(false);
      });
    });
  }, [supplierId]);

  useEffect(function () { load(); }, [load]);

  /* Lockout helper: any grower whose season has at least one submitted
     or accepted shipment in shipments[] is locked. We use the season
     year-start as a coarse match against the shipment's dateDeparture
     year — good enough for the gate; admins refine in their module. */
  const isLocked = useCallback(function (grower) {
    if (!grower || !grower.seasonYearFrom) return false;
    const targetYear = String(grower.seasonYearFrom);
    return shipments.some(function (s) {
      const stat = s.shipperStatus;
      if (stat !== "submitted" && stat !== "accepted") return false;
      const d = s.dateDeparture || s.dateArrival || "";
      return d.startsWith(targetYear);
    });
  }, [shipments]);

  const filtered = useMemo(function () {
    if (!search.trim()) return rows;
    const q = search.toLowerCase();
    return rows.filter(function (g) {
      return getFullName(g).toLowerCase().includes(q) || (g.producerCode || "").toLowerCase().includes(q);
    });
  }, [rows, search]);

  const th = { textAlign: "left", padding: "10px 12px", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--color-text-secondary)", borderBottom: "2px solid var(--color-border)" };
  const td = { padding: "10px 12px", borderBottom: "1px solid var(--color-border)", fontSize: 13 };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16, flexWrap: "wrap", gap: 12 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>{t.title}</h2>
          <p style={{ margin: "4px 0 0", fontSize: 13, color: "var(--color-text-muted)" }}>{t.sub}</p>
        </div>
        {canCreate && <button onClick={function () { setEditing("new"); }} style={{ padding: "9px 18px", borderRadius: 6, border: "none", background: "var(--color-accent)", color: "var(--color-accent-text)", cursor: "pointer", fontSize: 13, fontWeight: 700 }}>{t.add}</button>}
      </div>

      <div style={{ marginBottom: 12 }}>
        <input type="text" value={search} onChange={function (e) { setSearch(e.target.value); }} placeholder={t.search}
          style={{ padding: "9px 14px", fontSize: 13, border: "1px solid var(--color-border)", borderRadius: 6, background: "var(--color-bg-input)", color: "var(--color-text)", outline: "none", fontFamily: "inherit", width: 320, maxWidth: "100%" }} />
      </div>

      {loading ? (
        <div style={{ padding: 40, textAlign: "center", color: "var(--color-text-muted)" }}>…</div>
      ) : filtered.length === 0 ? (
        <div style={{ padding: 40, textAlign: "center", color: "var(--color-text-muted)", fontStyle: "italic" }}>{t.empty}</div>
      ) : (
        <div style={{ borderRadius: 10, border: "1px solid var(--color-border)", overflow: "hidden", background: "var(--color-bg-card)" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                {/* FIX 58 — select-all checkbox; operates only on
                    currently filtered rows. */}
                <th style={Object.assign({}, th, { width: 36, textAlign: "center", cursor: "default" })}>
                  <input type="checkbox"
                    checked={filtered.length > 0 && filtered.every(function (g) { return !!selected[g.id]; })}
                    onChange={function () {
                      var allOn = filtered.length > 0 && filtered.every(function (g) { return !!selected[g.id]; });
                      if (allOn) { setSelected({}); return; }
                      var next = {}; filtered.forEach(function (g) { next[g.id] = true; });
                      setSelected(next);
                    }} />
                </th>
                <th style={th}>{t.columns.name}</th>
                <th style={th}>{t.columns.code}</th>
                <th style={th}>{t.columns.season}</th>
                <th style={th}>{t.columns.products}</th>
                <th style={Object.assign({}, th, { textAlign: "right", width: 100 })}>{t.columns.hectares}</th>
                <th style={Object.assign({}, th, { textAlign: "center", width: 110 })}>{t.columns.compliance}</th>
                <th style={th}>{t.columns.status}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(function (g) {
                const locked = isLocked(g);
                /* FIX 20 — semáforo column.
                   Verde: required.length === 0 OR all satisfied.
                   Naranja: at least one satisfied + at least one missing.
                   Rojo: nothing satisfied. Tooltip lists missing items. */
                const cpl = evaluateGrowerCompliance(g, certifications, t);
                const dotColor = cpl.level === "green" ? "#059669" : cpl.level === "orange" ? "#F59E0B" : "#DC2626";
                const dotBg    = cpl.level === "green" ? "rgba(16,185,129,0.13)" : cpl.level === "orange" ? "rgba(245,158,11,0.13)" : "rgba(220,38,38,0.13)";
                const tooltip  = cpl.level === "green"
                  ? (lang === "es" ? "Todo en cumplimiento" : "All certifications on file")
                  : (lang === "es" ? "Faltan: " : "Missing: ") + cpl.missing.join(", ");
                return (
                  <tr key={g.id} style={{ cursor: "pointer" }} onClick={function () { setViewing(g); }}>
                    {/* FIX 58 — row checkbox. stopPropagation so a
                        click on the checkbox doesn't open the form. */}
                    <td style={Object.assign({}, td, { textAlign: "center" })} onClick={function (e) { e.stopPropagation(); }}>
                      <input type="checkbox"
                        checked={!!selected[g.id]}
                        onChange={function () {
                          setSelected(function (prev) {
                            var n = Object.assign({}, prev);
                            if (n[g.id]) delete n[g.id]; else n[g.id] = true;
                            return n;
                          });
                        }} />
                    </td>
                    <td style={Object.assign({}, td, { fontWeight: 600 })}>{getFullName(g)} {locked && <span title={t.locked} style={{ marginLeft: 6 }}>🔒</span>}</td>
                    <td style={Object.assign({}, td, { fontFamily: "'JetBrains Mono', monospace", color: "var(--color-accent)" })}>{g.producerCode || "—"}</td>
                    <td style={Object.assign({}, td, { fontFamily: "'JetBrains Mono', monospace", fontSize: 12 })}>{seasonLabel(g)}</td>
                    <td style={Object.assign({}, td, { fontSize: 12, color: "var(--color-text-secondary)" })}>{summarizeProducts(g)}</td>
                    <td style={Object.assign({}, td, { textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontSize: 12 })}>{(function () {
                      var total = (g.ranchos || []).reduce(function (s, r) { var n = parseFloat(r && r.hectares); return s + (isNaN(n) ? 0 : n); }, 0);
                      return total > 0 ? total.toFixed(2) : "—";
                    })()}</td>
                    <td style={Object.assign({}, td, { textAlign: "center" })} title={tooltip}>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "3px 9px", borderRadius: 10, fontSize: 11, fontWeight: 700, background: dotBg, color: dotColor }}>
                        <span style={{ width: 8, height: 8, borderRadius: 4, background: dotColor }} />
                        {cpl.satisfied}/{cpl.total || 0}
                      </span>
                    </td>
                    <td style={td} onClick={function (e) { e.stopPropagation(); }}>
                      {(function () {
                        /* Backward compat: si g.status no existe, isActive===false → "archived", else "active". */
                        var curStatus = g.status || (g.isActive === false ? "archived" : "active");
                        var statusColor = curStatus === "active" ? "#059669" : curStatus === "paused" ? "#D97706" : "#6B7280";
                        var statusBg = curStatus === "active" ? "rgba(16,185,129,0.13)" : curStatus === "paused" ? "rgba(245,158,11,0.13)" : "rgba(107,114,128,0.13)";
                        if (!canEdit) {
                          var statusLabel = curStatus === "active" ? t.status_active : curStatus === "paused" ? t.status_paused : t.status_archived;
                          return <span style={{ padding: "2px 8px", borderRadius: 8, fontSize: 10, fontWeight: 700, background: statusBg, color: statusColor }}>{statusLabel}</span>;
                        }
                        return (
                          <div style={{ minWidth: 110 }}>
                            <SearchSelect
                              value={curStatus}
                              onChange={async function (newStatus) {
                                if (!newStatus || newStatus === curStatus) return;
                                try {
                                  var updated = Object.assign({}, g, { status: newStatus, isActive: newStatus !== "archived" });
                                  await saveToSupabase("growers", updated);
                                  load();
                                } catch (err) { window.alert(String((err && err.message) || err)); }
                              }}
                              options={[
                                { value: "active", label: t.status_active },
                                { value: "paused", label: t.status_paused },
                                { value: "archived", label: t.status_archived },
                              ]}
                            />
                          </div>
                        );
                      })()}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {viewing && (
        <DetailPanel
          grower={viewing}
          t={t}
          lang={lang}
          certifications={certifications}
          canEdit={canEdit}
          onEdit={function (g) { setViewing(null); setEditing(g); }}
          onClose={function () { setViewing(null); }}
        />
      )}
      {editing && (
        <GrowerForm
          grower={editing === "new" ? null : editing}
          supplierId={supplierId}
          supplier={supplier}
          locked={editing !== "new" && isLocked(editing)}
          certifications={certifications}
          canDelete={canDelete && editing !== "new" && !growerHasActiveSeasonShipments(editing, shipments)}
          onDelete={async function () {
            if (editing === "new") return;
            var hasShipments = growerHasActiveSeasonShipments(editing, shipments);
            var msg = hasShipments
              ? (lang === "es" ? "¿Desactivar este productor? Mantiene su histórico pero ya no aparece como activo." : "Deactivate this grower? Their history stays but they no longer appear active.")
              : (lang === "es" ? "¿Eliminar este productor? Esta acción no se puede deshacer." : "Delete this grower? This cannot be undone.");
            if (!window.confirm(msg)) return;
            try {
              if (hasShipments) {
                /* Soft-delete by flipping isActive to false. */
                var deactivated = Object.assign({}, editing, { isActive: false });
                await saveToSupabase("growers", deactivated);
              } else {
                /* Hard-delete when no active-season work exists. */
                var mod = await import("../../supabaseDB");
                await mod.deleteFromSupabase("growers", editing.id);
              }
              setEditing(null);
              load();
            } catch (e) { window.alert(String((e && e.message) || e)); }
          }}
          t={t}
          lang={lang}
          onSave={function () { setEditing(null); load(); }}
          onCancel={function () { setEditing(null); }}
          onRequestChange={function () { setRequesting(editing); }}
        />
      )}
      {requesting && (
        <RequestChangeModal
          grower={requesting}
          supplierId={supplierId}
          profile={profile}
          t={t}
          lang={lang}
          onSent={function () { setRequesting(null); load(); }}
          onClose={function () { setRequesting(null); }}
        />
      )}
    </div>
  );
}
